// Calcul des heures de travail. Repères légaux (service-public.gouv.fr, secteur
// privé, salarié à temps plein) : 35 h par semaine, 10 h par jour au plus, 48 h
// sur une semaine, pause de 20 min dès 6 h de travail, heures supplémentaires
// majorées de 25 % de la 36e à la 43e heure puis de 50 %, sauf accord collectif.

export const LEGAL_WEEK_MIN = 35 * 60;
export const MAX_DAY_MIN = 10 * 60;
export const MAX_WEEK_MIN = 48 * 60;
export const PAUSE_THRESHOLD_MIN = 6 * 60;
export const MIN_PAUSE_MIN = 20;

// Plage de nuit par défaut (Code du travail : 21 h – 6 h). Dans la fonction
// publique hospitalière, 9 h consécutives entre 21 h et 7 h : 21 h – 6 h ou 22 h – 7 h.
export const NIGHT_WINDOWS = {
  "21-6": { label: "21\u00a0h\u00a0–\u00a06\u00a0h", start: 21 * 60, end: 6 * 60 },
  "22-7": { label: "22\u00a0h\u00a0–\u00a07\u00a0h", start: 22 * 60, end: 7 * 60 },
};

// "HH:MM" -> minutes depuis minuit, ou null.
export function parseTime(s) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(s || "").trim());
  if (!m) return null;
  const h = Number(m[1]), min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

function overlap(a1, a2, b1, b2) {
  return Math.max(0, Math.min(a2, b2) - Math.max(a1, b1));
}

// Minutes de présence dans la plage de nuit, pour un service de start à end
// (end < start : le service finit le lendemain). La pause n'est pas déduite :
// on ne sait pas à quelle heure elle est prise.
export function nightMinutes(start, end, windowKey = "21-6") {
  const w = NIGHT_WINDOWS[windowKey];
  // Plages de nuit sur deux jours consécutifs : [0, fin], [début, fin + 24 h], [début + 24 h, 48 h].
  const nights = [[0, w.end], [w.start, w.end + 1440], [w.start + 1440, 2880]];
  return nights.reduce((sum, [a, b]) => sum + overlap(start, end, a, b), 0);
}

// Un jour : { start: "HH:MM", end: "HH:MM", pause: minutes }.
export function computeDay({ start, end, pause }) {
  const s = parseTime(start), e0 = parseTime(end);
  if (s === null || e0 === null) return null;
  const overnight = e0 <= s && e0 !== s;
  const e = overnight ? e0 + 1440 : e0;
  const span = e - s;
  const p = Math.max(0, Math.round(Number(pause) || 0));
  if (span === 0) return null;
  if (p >= span) return { error: "La pause dépasse la durée de présence." };
  return { span, pause: p, worked: span - p, overnight, startMin: s, endMin: e };
}

export function computeWeek(days, { windowKey = "21-6" } = {}) {
  const results = days.map(computeDay);
  const valid = results.filter((r) => r && !r.error);
  const total = valid.reduce((sum, r) => sum + r.worked, 0);
  const night = valid.reduce((sum, r) => sum + nightMinutes(r.startMin, r.endMin, windowKey), 0);
  const extra = Math.max(0, total - LEGAL_WEEK_MIN);
  const hs25 = Math.min(extra, 8 * 60);
  const hs50 = extra - hs25;
  return {
    days: results,
    total,
    night,
    daysWorked: valid.length,
    hs25,
    hs50,
    monthly: (total * 52) / 12,
    alerts: {
      longDays: results.map((r, i) => (r && !r.error && r.worked > MAX_DAY_MIN ? i : -1)).filter((i) => i >= 0),
      shortPause: results.map((r, i) => (r && !r.error && r.worked > PAUSE_THRESHOLD_MIN && r.pause < MIN_PAUSE_MIN ? i : -1)).filter((i) => i >= 0),
      overWeek: total > MAX_WEEK_MIN,
    },
  };
}

// Salaire brut estimé : heures normales au taux horaire, heures supplémentaires
// majorées de 25 % puis 50 % (taux légaux par défaut).
export function grossPay(week, hourlyRate) {
  if (!(hourlyRate > 0)) return null;
  const normal = week.total - week.hs25 - week.hs50;
  return ((normal + week.hs25 * 1.25 + week.hs50 * 1.5) / 60) * hourlyRate;
}

// 450 -> "7 h 30" ; décimal (centièmes) -> "7,50".
export const formatHM = (min) => `${Math.floor(min / 60)} h ${String(Math.round(min % 60)).padStart(2, "0")}`;
export const formatDecimal = (min) => (Math.round((min / 60) * 100) / 100).toFixed(2).replace(".", ",");
